// A single playfield: hold + board + queue + HUD. Pixi v8 scene graph.
//
// Used once for solo modes and twice (side-by-side) for Versus.

import {
  BlurFilter, Container, FillGradient, Graphics, Sprite, Text, Texture,
} from 'pixi.js';
import gsap from 'gsap';

import {
  BOARD_BUFFER_HEIGHT, BOARD_VISIBLE_HEIGHT, BOARD_WIDTH, LOCK_DELAY_MS,
} from '../config';
import type { Engine } from '../game/engine';
import { shapeOf } from '../game/pieces';
import type { PieceType, Rotation } from '../types';
import type { ThemePalette } from '../render/palettes';
import type { RenderOptions } from '../render/renderer';
import { formatTime } from '../modes/modes';
import { generateCellTextures, type CellTextureSet } from './textures';
import type { Application } from 'pixi.js';

// Lazily-loaded textures for the hold-box lock icons. We rasterize the
// project's lock SVGs as Pixi textures once and reuse them across every
// playfield instance. currentColor in the SVG renders as black; we tint
// via Sprite.tint per palette.
let _lockOpenTex: Texture | null = null;
let _lockClosedTex: Texture | null = null;
function getHoldLockOpenTex(): Texture {
  if (!_lockOpenTex) _lockOpenTex = Texture.from('/icons/unlock-alt-solid.svg');
  return _lockOpenTex;
}
function getHoldLockClosedTex(): Texture {
  if (!_lockClosedTex) _lockClosedTex = Texture.from('/icons/lock-alt-solid.svg');
  return _lockClosedTex;
}

export interface PlayfieldOptions {
  cellPx: number;
  previewCount: number;
  showHold: boolean;
  showNext: boolean;
  showHud: boolean;
  /** Compact mode (versus) hides some HUD detail. */
  compact: boolean;
}

export class Playfield {
  app: Application;
  palette: ThemePalette;
  options: PlayfieldOptions;
  renderOpts: RenderOptions;
  /** Snapshot of the last-applied cell style so subsequent setRenderOptions
   *  calls can diff reliably even when the caller mutates the shared
   *  settings object in place. */
  private lastAppliedStyle: string;
  textures!: CellTextureSet;
  engine: Engine | null = null;

  /** Root container — caller positions this within the stage. */
  root = new Container();

  // Layers within root
  bgLayer = new Container();
  boardLayer = new Container();
  // Ink group — wraps everything we want chromatic aberration to affect
  // (the cells, ghost, active piece, line flashes, attack/garbage effects).
  // The HUD text, board frame, and accolades sit OUTSIDE inkLayer so they
  // stay crisp regardless of chromatic intensity.
  inkLayer = new Container();
  cellsLayer = new Container();
  ghostLayer = new Container();
  pieceLayer = new Container();
  effectsLayer = new Container();
  flashLayer = new Container();
  // HUD + accolades start hidden — they're reparented out of root by
  // PixiApp, so root.visible doesn't gate them. setEngine() flips
  // them on once an engine is attached, off when it's cleared.
  hudLayer = new Container();
  accoladesLayer = new Container();

  // Persistent visuals
  private boardFrame = new Graphics();
  private boardBg = new Graphics();
  private gridLines = new Graphics();
  /** Mask that clips piece / ghost / cells to the board bounds. Sprites
   *  positioned in the buffer (y < 0) won't bleed up past the board. */
  private boardMask = new Graphics();
  // [row][col] sprite for stamped cells; reused as the board state changes.
  private stampedSprites: (Sprite | null)[][] = [];
  // Active piece sprites (rebuilt on piece spawn / rotation).
  private activeSprites: Sprite[] = [];
  // Ghost piece sprites.
  private ghostSprites: Sprite[] = [];

  // Visual piece state — what the cells are drawn at; lerped toward logical.
  private visual = { x: 0, y: 0 };
  private rotationVisual = 0;
  private lastSeq = -1;
  private moveTween: gsap.core.Tween | null = null;
  private rotateTween: gsap.core.Tween | null = null;
  /** Per-tick velocity-following for the smooth glide; used as a damped spring fallback. */

  // HUD elements
  private holdBg = new Graphics();
  private holdLabel: Text;
  /** Lock-state icon shown in the top-left of the hold box. Texture is
   *  one of `lockOpenTex` / `lockClosedTex` (loaded lazily on first
   *  access), or hidden when the hold slot is empty. */
  private holdLockSprite!: Sprite;
  /** Background container for the clears feed — drawn like the hold /
   *  next boxes so the feed reads as part of the chrome instead of
   *  floating text. Header shows 'mode: <name>'. */
  private feedBg = new Graphics();
  private feedHeaderText!: Text;
  /** Background container for the metrics column — wraps the
   *  incoming / level / lines / pieces / pps stack in the same chrome
   *  treatment as the hold / next / feed boxes. */
  private metricsBg = new Graphics();
  /** Header text 'stats' at the top of the metrics box — matches the
   *  feedHeaderText typography so each sidebar box reads as a labeled
   *  container. */
  private statsHeaderText!: Text;
  private nextBg = new Graphics();
  private nextLabels: Graphics[] = [];
  private modeText: Text;
  private modeValueText: Text;
  private metricLabelText: Text;
  private metricValueText: Text;
  private detailLines: { label: Text; value: Text }[] = [];
  private comboChip: Text;
  private b2bChip: Text;
  private holdSprites: Sprite[] = [];
  private nextSpritesPerSlot: Sprite[][] = [];

  // Accolade pool
  private accoladePool: Text[] = [];
  // Centered overlay text (countdown / GO / TOP OUT). May be a single Text
  // (setOverlayText) or a Container with several layered children
  // (dramaticOverlay). destroy({ children: true }) covers both.
  private overlayText: Container | Text | null = null;

  // Shake offset
  private shakeAmount = 0;
  private shakeTtl = 0;
  private shakeBaseX = 0;
  private shakeBaseY = 0;

  // Last drawn line-flash rows (in board y) and ttl
  private flashTtl = 0;

  constructor(app: Application, palette: ThemePalette, options: PlayfieldOptions, renderOpts: RenderOptions) {
    this.app = app;
    this.palette = palette;
    this.options = options;
    // Snapshot the incoming options so external mutations of the shared
    // settings object don't reach into our private state.
    this.renderOpts = { ...renderOpts };
    this.lastAppliedStyle = renderOpts.rgbCellStyle;
    this.regenerateTextures();
    // Stack the layers (z order = add order). The visual elements that
    // should receive chromatic aberration live inside inkLayer; the board
    // chrome / HUD / accolades sit outside so they stay legible.
    this.inkLayer.addChild(
      this.cellsLayer,
      this.ghostLayer,
      this.pieceLayer,
      this.flashLayer,
      this.effectsLayer,
    );
    this.root.addChild(
      this.bgLayer,
      this.boardLayer,
      this.inkLayer,
      this.hudLayer,
      this.accoladesLayer,
    );
    // Match root's default-hidden state — PixiApp reparents these to
    // hudRoot (outside the filter chain), so root.visible doesn't
    // cascade to them. setEngine() flips them in lockstep with root.
    this.root.visible = false;
    this.hudLayer.visible = false;
    this.accoladesLayer.visible = false;
    this.boardLayer.addChild(this.boardBg, this.boardFrame, this.gridLines);
    // Mask sits in the scene graph (required by Pixi) but is invisible.
    this.boardLayer.addChild(this.boardMask);
    this.cellsLayer.mask = this.boardMask;
    this.ghostLayer.mask = this.boardMask;
    this.pieceLayer.mask = this.boardMask;
    this.flashLayer.mask = this.boardMask;
    this.hudLayer.addChild(this.holdBg, this.nextBg, this.feedBg, this.metricsBg);
    // Pre-allocate stamped grid
    for (let y = 0; y < BOARD_VISIBLE_HEIGHT; y++) {
      const row: (Sprite | null)[] = [];
      for (let x = 0; x < BOARD_WIDTH; x++) row.push(null);
      this.stampedSprites.push(row);
    }
    // Hold-box header — shows 'held' when a piece is in the slot and
    // 'locked' when the hold is locked. Matches the feed + stats box
    // header typography so the three sidebar containers read uniformly.
    this.holdLabel = this.makeText('held', 10, palette.textDim, 0.18);
    this.hudLayer.addChild(this.holdLabel);
    // Lock-state sprite. Texture is swapped at runtime between the
    // open / closed lock icons; sprite is added/removed from the hud
    // layer in updateHold() based on whether the hold slot has a piece
    // and whether the player has already swapped this piece.
    this.holdLockSprite = new Sprite();
    this.holdLockSprite.visible = false;
    this.holdLockSprite.width = 14;
    this.holdLockSprite.height = 14;
    this.hudLayer.addChild(this.holdLockSprite);
    // Feed-box header text — sits at the top of the feed background and
    // shows 'mode: <name>'. updated by setHudData().
    this.feedHeaderText = this.makeText('', 10, palette.textDim, 0.18);
    this.hudLayer.addChild(this.feedHeaderText);
    // Stats-box header — matches the feed header style. Static label.
    this.statsHeaderText = this.makeText('stats', 10, palette.textDim, 0.18);
    this.hudLayer.addChild(this.statsHeaderText);
    this.modeText = this.makeText('mode', 11, palette.textDim, 0.18);
    this.modeValueText = this.makeText('', 18, palette.text, 0.10, '700');
    this.metricLabelText = this.makeText('', 11, palette.textDim, 0.18);
    this.metricValueText = this.makeText('', 18, palette.accent2, 0.10, '700');
    this.hudLayer.addChild(this.modeText, this.modeValueText, this.metricLabelText, this.metricValueText);
    const labels = ['level', 'lines', 'pieces', 'pps'];
    for (const lab of labels) {
      const label = this.makeText(lab, 10, palette.textDim, 0.18);
      const value = this.makeText('0', 16, palette.text, 0.10, '700');
      this.detailLines.push({ label, value });
      this.hudLayer.addChild(label, value);
    }
    this.comboChip = this.makeText('', 11, palette.accent2, 0.18, '700');
    this.b2bChip = this.makeText('', 11, palette.accent, 0.18, '700');
    this.hudLayer.addChild(this.comboChip, this.b2bChip);
  }

  destroy(): void {
    this.moveTween?.kill();
    this.rotateTween?.kill();
    // PixiApp re-parents hudLayer + accoladesLayer to its hudRoot
    // outside the filtered scene; root.destroy doesn't traverse them
    // anymore, so destroy them explicitly to avoid a leak.
    this.hudLayer.destroy({ children: true });
    this.accoladesLayer.destroy({ children: true });
    this.root.destroy({ children: true });
  }

  /** Apply (or clear) the chromatic aberration filter on the ink layer.
   *  The App passes the shared RGBSplitFilter instance so every board
   *  splits in lockstep. Pass null to disable. */
  setChromaticFilter(filter: import('pixi.js').Filter | null): void {
    this.inkLayer.filters = filter ? [filter] : null;
  }

  setEngine(e: Engine | null): void {
    this.engine = e;
    this.lastSeq = -1;
    this.clearStamped();
    this.activeSprites.forEach((s) => s.destroy());
    this.activeSprites = [];
    this.ghostSprites.forEach((s) => s.destroy());
    this.ghostSprites = [];
    // Critical: wipe any lingering 'topped out' / 'complete' overlay from
    // the previous run so it doesn't ghost onto the next board.
    this.clearOverlays();
    // Reset the clears feed so events from the previous run don't carry
    // over into the next.
    for (const e2 of this.feedEntries) e2.node.destroy();
    this.feedEntries = [];
    // Hide the entire playfield (board frame + grid + hud + hold/next) when
    // no engine is attached — otherwise the chrome lingers visibly behind
    // the main menu when the user quits a run. hudLayer + accoladesLayer
    // are now siblings (parented to PixiApp.hudRoot, outside the filter
    // chain), so root.visible alone doesn't hide them — set their
    // visibility explicitly.
    const visible = e !== null;
    this.root.visible = visible;
    this.hudLayer.visible = visible;
    this.accoladesLayer.visible = visible;
  }

  setPalette(p: ThemePalette): void {
    this.palette = p;
    this.regenerateTextures();
    this.modeText.style.fill = p.textDim;
    this.metricLabelText.style.fill = p.textDim;
    this.metricValueText.style.fill = p.accent2;
    this.holdLabel.style.fill = p.textDim;
    this.comboChip.style.fill = p.accent2;
    this.b2bChip.style.fill = p.accent;
    for (const d of this.detailLines) {
      d.label.style.fill = p.textDim;
      d.value.style.fill = p.text;
    }
    // Force redraw of everything
    this.clearStamped();
    this.activeSprites.forEach((s) => s.destroy());
    this.activeSprites = [];
    this.ghostSprites.forEach((s) => s.destroy());
    this.ghostSprites = [];
    this.lastSeq = -1;
    // Re-run layout so the board background, frame, grid lines, and
    // hold/next backgrounds repaint with the new palette colors. Without
    // this they keep the old fill until the next window resize.
    this.layout();
  }

  setRenderOptions(o: RenderOptions): void {
    // Compare against our cached prior style. If the caller passed the same
    // object reference (the settings struct gets mutated in place by the
    // theme picker, then this exact reference is forwarded here), reading
    // `this.renderOpts.rgbCellStyle` would already see the new value — so
    // we keep a separate snapshot of the previous style for the diff.
    const prevStyle = this.lastAppliedStyle;
    // Snapshot incoming options into our own object so future external
    // mutations don't sneak past the diff.
    this.renderOpts = { ...o };
    this.lastAppliedStyle = o.rgbCellStyle;
    if (prevStyle !== o.rgbCellStyle) {
      this.regenerateTextures();
      this.clearStamped();
      this.activeSprites.forEach((s) => s.destroy());
      this.activeSprites = [];
      this.ghostSprites.forEach((s) => s.destroy());
      this.ghostSprites = [];
      this.lastSeq = -1;
    }
  }

  setOptions(o: Partial<PlayfieldOptions>): void {
    this.options = { ...this.options, ...o };
  }

  private regenerateTextures(): void {
    this.textures = generateCellTextures(this.app, this.palette, this.renderOpts.rgbCellStyle, 64);
  }

  /** Trigger a screen-shake on this playfield only. */
  shake(strength: number, durMs = 220): void {
    this.shakeAmount = Math.max(this.shakeAmount, strength);
    this.shakeTtl = Math.max(this.shakeTtl, durMs);
  }

  /** Recent clears feed — a stack of text entries inside the dedicated
   *  feed box (sidebar, between hold and stats). New entries enter at
   *  the top of the box (just below the 'mode: <name>' header), push
   *  older entries down with a tween, fade progressively toward the
   *  bottom. Capped at 5 visible entries (oldest fades out). */
  /** Stats box bottom edge (local y) — miniplayer aligns to this. */
  private metricsBottomY = 0;
  private feedEntries: { node: Text; }[] = [];
  private readonly FEED_MAX = 5;
  pushFeedEntry(text: string, color: string): void {
    if (!this.options.showHud) return;
    const ls = this.layoutState;
    if (!ls) return;
    const cellPx = this.options.cellPx;
    const t = this.makeText(text.toLowerCase(), 11, color, 0.10, '700');
    // Defensive truncation — the feed column is sized in cell units
    // (~4 cells minus a small inset), so long event names like
    // 'mini t-spin double' would otherwise spill past the box edge
    // at smaller cellPx (versus / triple / royal layouts). Trim
    // character-by-character with a trailing ellipsis until the
    // rendered width fits the visible column.
    const sideW = Math.round(cellPx * 4);
    const maxTextWidth = sideW - 16;
    if (t.width > maxTextWidth) {
      let s = text.toLowerCase();
      while (s.length > 3 && t.width > maxTextWidth) {
        s = s.slice(0, -1);
        t.text = `${s}…`;
      }
    }
    t.x = ls.holdX + 8;
    t.y = Math.round(cellPx * 3.6) + 26;
    t.alpha = 0;
    this.hudLayer.addChild(t);
    this.feedEntries.unshift({ node: t });
    this.layoutFeed();
    while (this.feedEntries.length > this.FEED_MAX) {
      const old = this.feedEntries.pop()!;
      gsap.to(old.node, {
        alpha: 0, duration: 0.30, ease: 'power2.in',
        onComplete: () => old.node.destroy(),
      });
    }
  }

  private layoutFeed(): void {
    const ls = this.layoutState;
    if (!ls) return;
    const cellPx = this.options.cellPx;
    const slotPx = Math.max(14, cellPx * 0.42);
    // Anchor under the feed-box header (header text sits at feedY + 6,
    // ~14px tall, plus a small gap → ~26px below the box top).
    const feedTopY = Math.round(cellPx * 3.6);
    const baseY = feedTopY + 26;
    for (let i = 0; i < this.feedEntries.length; i++) {
      const e = this.feedEntries[i]!;
      const y = baseY + i * slotPx;
      // Progressive fade from top (full opacity) to bottom (20%),
      // mirroring how the next-piece queue feathers downstream items.
      const fade = Math.max(0.20, 1 - i * 0.20);
      gsap.to(e.node, {
        x: ls.holdX + 8,
        y, alpha: fade,
        duration: 0.34, ease: 'power2.out',
      });
    }
  }

  /** Pulse a brief motion blur on the active piece layer. Used to add
   *  a tactile flicker to rotate (uniform low blur) and hard-drop
   *  (stronger vertical blur). The filter is removed once strength
   *  tweens back to 0 so there's no ongoing GPU cost. */
  private pieceBlurFilter: BlurFilter | null = null;
  private pieceBlurTween: gsap.core.Tween | null = null;
  pulsePieceBlur(strengthX: number, strengthY: number, durationMs = 140): void {
    if (!this.pieceBlurFilter) {
      this.pieceBlurFilter = new BlurFilter({ strength: 0, quality: 2 });
    }
    this.pieceBlurFilter.strengthX = strengthX;
    this.pieceBlurFilter.strengthY = strengthY;
    this.pieceLayer.filters = [this.pieceBlurFilter];
    this.pieceBlurTween?.kill();
    this.pieceBlurTween = gsap.to(this.pieceBlurFilter, {
      strengthX: 0, strengthY: 0,
      duration: durationMs / 1000, ease: 'power2.out',
      onComplete: () => { this.pieceLayer.filters = null; },
    });
  }

  /** Quick lateral shake on the hold box + its piece sprites. Used when
   *  the player tries to hold while the slot is locked (already used this
   *  piece). Pairs with the attack_receive sfx so the rejection is felt. */
  shakeHold(): void {
    const tl = gsap.timeline();
    const targets = [this.holdBg, this.holdLockSprite, ...this.holdSprites];
    const baseX = targets.map((t) => t.x);
    for (let i = 0; i < 4; i++) {
      const dx = i % 2 === 0 ? -4 : 4;
      tl.to(targets, { x: (j: number) => baseX[j]! + dx, duration: 0.04, ease: 'none' });
    }
    tl.to(targets, { x: (j: number) => baseX[j]!, duration: 0.06, ease: 'power2.out' });
  }

  /** Flash a glowing rim around the board — used to telegraph an incoming
   *  garbage attack in versus mode. Color defaults to palette.danger. */
  flashBoardRim(color?: string, durMs = 280): void {
    const ls = this.layoutState;
    if (!ls) return;
    const c = parseInt((color ?? this.palette.danger).replace('#', '').slice(0, 6), 16);
    const rim = new Graphics();
    const pad = 3;
    // accoladesLayer is unmasked; placing the rim there means the outer
    // glow isn't clipped by the board bounds.
    rim.roundRect(-pad, -pad, ls.boardW + pad * 2, ls.boardH + pad * 2, 5)
      .stroke({ width: 4, color: c, alpha: 0.95 });
    // accoladesLayer is already positioned at (boardX, boardY) by layout()
    // so children draw in board-local coordinates.
    this.accoladesLayer.addChild(rim);
    gsap.to(rim, { alpha: 0, duration: durMs / 1000, ease: 'power2.out', onComplete: () => rim.destroy() });
  }

  /** Line-clear animation. A single sharp flash on each cleared row pulses
   *  from accent-tinted to transparent in ~180ms with a slight vertical
   *  scale-out — feels snappier and more contained than the previous full-
   *  width sweep beam. Total runtime ~220ms so it never blocks the next
   *  piece spawning. */
  flashRows(rows: number[]): void {
    if (!rows.length) return;
    this.flashTtl = 180;
    const cellPx = this.options.cellPx;
    const accent = parseInt(this.palette.accent.slice(1), 16);
    const accent2 = parseInt(this.palette.accent2.slice(1), 16);
    for (const r of rows) {
      const visY = r - BOARD_BUFFER_HEIGHT;
      if (visY < 0 || visY >= BOARD_VISIBLE_HEIGHT) continue;
      // Two-layer flash: a bright white core + an accent-tinted outer
      // halo. Both pulse + collapse vertically so the row feels like it
      // disintegrates rather than wiping sideways.
      //
      // IMPORTANT: rect is drawn in the Graphics's LOCAL space (origin 0,0).
      // The Graphics position (.y) is what places it on the cleared row.
      // Previously the rect was drawn at local y=visY*cellPx AND the
      // Graphics was positioned at visY*cellPx — doubling the offset, so
      // every flash slid down to the bottom of the board.
      const halo = new Graphics();
      halo.rect(0, -cellPx / 2, BOARD_WIDTH * cellPx, cellPx)
        .fill({ color: accent, alpha: 0.55 });
      halo.alpha = 0;
      halo.y = visY * cellPx + cellPx / 2;
      this.flashLayer.addChild(halo);
      const core = new Graphics();
      core.rect(0, -cellPx * 0.20, BOARD_WIDTH * cellPx, cellPx * 0.40)
        .fill({ color: 0xffffff, alpha: 0.85 });
      core.tint = accent2;
      core.alpha = 0;
      core.y = visY * cellPx + cellPx / 2;
      this.flashLayer.addChild(core);
      gsap.timeline()
        .to(halo, { alpha: 0.95, duration: 0.04, ease: 'power3.out' }, 0)
        .to(core, { alpha: 1.0, duration: 0.04, ease: 'power3.out' }, 0)
        .to(halo, { alpha: 0, duration: 0.18, ease: 'power2.in' }, 0.04)
        .to(core, { alpha: 0, duration: 0.16, ease: 'power2.in' }, 0.06)
        // Collapse vertically — quick "iris" close that makes the row
        // visually disappear into a horizontal line before fading.
        .to([halo.scale, core.scale], { y: 0.08, duration: 0.20, ease: 'power3.in' }, 0.02)
        .call(() => { halo.destroy(); core.destroy(); });
    }
  }

  /** Big centred countdown / status text with arcade flair. Each beat
   *  spawns a multi-layer animation: expanding pixel-cell frame, halo
   *  ring, drifting particle squares, and the title text itself. */
  setOverlayText(text: string | null, opts?: { color?: string; subtitle?: string; bigPulse?: boolean }): void {
    if (this.overlayText) { this.overlayText.destroy({ children: true }); this.overlayText = null; }
    if (!text) return;
    const cellPx = this.options.cellPx;
    const color = opts?.color ?? this.palette.accent;
    const lum = textLuminance(color);
    const strokeColor = lum < 0.5 ? 0xffffff : 0x000000;
    const shadowColor = lum < 0.5 ? 0xffffff : color;
    const colorHex = parseInt(color.replace('#', '').slice(0, 6), 16);
    const accentHex = parseInt(this.palette.accent.replace('#', '').slice(0, 6), 16);
    const accent2Hex = parseInt(this.palette.accent2.replace('#', '').slice(0, 6), 16);

    const group = new Container();
    group.x = (BOARD_WIDTH * cellPx) / 2;
    group.y = (BOARD_VISIBLE_HEIGHT * cellPx) / 2;

    // Pixel-cell frame around the text — four corner brackets that fly in
    // from offscreen, giving the announcement an arcade UI feel.
    const titleSize = Math.max(28, cellPx * 2.0);
    const frameHalfW = Math.max(75, titleSize * (text.length * 0.55));
    const frameHalfH = titleSize * 0.95;
    const bracketLen = Math.max(14, titleSize * 0.45);
    const bracketThk = Math.max(3, cellPx * 0.14);
    const brackets: Graphics[] = [];
    const corners = [
      { x: -frameHalfW, y: -frameHalfH, dx: 1,  dy: 1  },  // top-left
      { x:  frameHalfW, y: -frameHalfH, dx: -1, dy: 1  },  // top-right
      { x: -frameHalfW, y:  frameHalfH, dx: 1,  dy: -1 },  // bot-left
      { x:  frameHalfW, y:  frameHalfH, dx: -1, dy: -1 },  // bot-right
    ];
    for (const c of corners) {
      const br = new Graphics();
      br.rect(0, 0, bracketLen * c.dx, bracketThk).fill({ color: colorHex, alpha: 0.95 });
      br.rect(0, 0, bracketThk, bracketLen * c.dy).fill({ color: colorHex, alpha: 0.95 });
      br.x = c.x; br.y = c.y;
      br.alpha = 0;
      brackets.push(br);
      group.addChild(br);
    }

    // Two expanding rings — accent + accent2, slightly offset in time,
    // so the beat has a layered visual punctuation rather than a single
    // flat circle.
    const ringSize = Math.max(60, cellPx * 3.4);
    const ring = new Graphics();
    ring.circle(0, 0, ringSize).stroke({ width: 3, color: accentHex, alpha: 0.85 });
    ring.alpha = 0;
    ring.scale.set(0.5);
    group.addChild(ring);
    const ring2 = new Graphics();
    ring2.circle(0, 0, ringSize * 0.7).stroke({ width: 2, color: accent2Hex, alpha: 0.7 });
    ring2.alpha = 0;
    ring2.scale.set(0.5);
    group.addChild(ring2);

    // 12 little pixel-square particles that fly outward + fade. They use
    // a mix of accent / accent2 / palette piece colors so the burst has
    // colour, not just a single hue.
    const particles: Graphics[] = [];
    const partColors = [accentHex, accent2Hex,
      parseInt(this.palette.pieces.O.replace('#', '').slice(0, 6), 16),
      parseInt(this.palette.pieces.S.replace('#', '').slice(0, 6), 16),
    ];
    const partSize = Math.max(4, cellPx * 0.22);
    for (let i = 0; i < 12; i++) {
      const part = new Graphics();
      const pc = partColors[i % partColors.length]!;
      part.rect(-partSize / 2, -partSize / 2, partSize, partSize).fill({ color: pc, alpha: 1 });
      part.alpha = 0;
      group.addChild(part);
      particles.push(part);
    }

    // Main pixel text.
    const t = new Text({
      text: text.toUpperCase(),
      style: {
        fontFamily: '"Bugvirus", "CS Modley Pixel", "Hack", monospace',
        fontSize: titleSize,
        fontWeight: '400',
        fill: color,
        align: 'center',
        letterSpacing: 0,
        dropShadow: { color: shadowColor, alpha: 0.7, blur: 0, distance: 4, angle: Math.PI / 2 },
        stroke: { color: strokeColor, width: 3, alpha: 0.9 },
      },
    });
    t.anchor.set(0.5);
    group.addChild(t);

    if (opts?.bigPulse) {
      t.scale.set(0.5);
      t.alpha = 0;
      gsap.to(t, { alpha: 1, duration: 0.18, ease: 'power2.out' });
      gsap.to(t.scale, { x: 1.0, y: 1.0, duration: 0.40, ease: 'back.out(1.8)' });
      gsap.to(t.scale, { x: 1.04, y: 1.04, duration: 1.4, ease: 'sine.inOut', repeat: -1, yoyo: true, delay: 0.45 });
      // Brackets fly in from offscreen corners + settle.
      brackets.forEach((br, i) => {
        const c = corners[i]!;
        gsap.fromTo(br,
          { alpha: 0, x: c.x + c.dx * -40, y: c.y + c.dy * -40 },
          { alpha: 1, x: c.x, y: c.y, duration: 0.32, ease: 'back.out(1.6)', delay: i * 0.03 });
      });
      // Rings sweep outward and fade, slightly staggered.
      gsap.to(ring, { alpha: 0.85, duration: 0.10, ease: 'power2.out' });
      gsap.to(ring.scale, { x: 1.4, y: 1.4, duration: 0.55, ease: 'power3.out' });
      gsap.to(ring, { alpha: 0, duration: 0.55, ease: 'power2.in', delay: 0.20 });
      gsap.to(ring2, { alpha: 0.7, duration: 0.10, ease: 'power2.out', delay: 0.06 });
      gsap.to(ring2.scale, { x: 1.7, y: 1.7, duration: 0.6, ease: 'power3.out', delay: 0.06 });
      gsap.to(ring2, { alpha: 0, duration: 0.55, ease: 'power2.in', delay: 0.30 });
      // Particle burst — each cube flies outward at a unique angle.
      particles.forEach((part, i) => {
        const angle = (i / particles.length) * Math.PI * 2;
        const dist = ringSize * (0.8 + Math.random() * 0.7);
        const dx = Math.cos(angle) * dist;
        const dy = Math.sin(angle) * dist;
        gsap.fromTo(part,
          { alpha: 0, x: 0, y: 0 },
          { alpha: 1, duration: 0.06, ease: 'power2.out' });
        gsap.to(part, {
          x: dx, y: dy, alpha: 0, duration: 0.85, ease: 'power3.out',
          delay: 0.02 + Math.random() * 0.04,
        });
        gsap.to(part, {
          rotation: (Math.random() - 0.5) * Math.PI * 1.5, duration: 0.85, ease: 'power3.out',
        });
      });
    }
    this.accoladesLayer.addChild(group);
    this.overlayText = group;
  }

  /** Big match-end overlay — multi-layer animated treatment with character.
   *  Used for top-out / time-up / complete. */
  /** Minimal in-board overlay used for AI-side end-of-match marks. The
   *  player-side end overlay now lives in DOM (see App.showEndOverlay)
   *  so the bracket/ring chrome that used to live here is gone — this is
   *  just a clean centered text fade for the per-AI death tag. */
  dramaticOverlay(text: string, kind: 'topout' | 'finish'): void {
    if (this.overlayText) { this.overlayText.destroy({ children: true }); this.overlayText = null; }
    const cellPx = this.options.cellPx;
    const cx = (BOARD_WIDTH * cellPx) / 2;
    const cy = (BOARD_VISIBLE_HEIGHT * cellPx) / 2;
    const color = kind === 'topout' ? this.palette.danger : '#ffd23f';
    const titleHeight = Math.max(20, cellPx * 1.4);

    const group = new Container();
    group.x = cx; group.y = cy;
    this.accoladesLayer.addChild(group);

    const wash = new Graphics();
    const halfW = (BOARD_WIDTH * cellPx) / 2;
    const halfH = (BOARD_VISIBLE_HEIGHT * cellPx) / 2;
    wash.rect(-halfW, -halfH, halfW * 2, halfH * 2).fill({ color: 0x000000, alpha: 0.45 });
    wash.alpha = 0;
    group.addChild(wash);

    const main = new Text({
      text: text.toLowerCase(),
      style: {
        fontFamily: '"Bugvirus", "CS Modley Pixel", "Hack", monospace',
        fontSize: titleHeight,
        fontWeight: '400',
        fill: color,
        align: 'center',
        letterSpacing: 1,
        stroke: { color: 0x000000, width: 2, alpha: 0.7 },
      },
    });
    main.anchor.set(0.5);
    main.alpha = 0;
    main.scale.set(0.7);
    group.addChild(main);

    gsap.timeline()
      .to(wash, { alpha: 1, duration: 0.18, ease: 'power2.out' }, 0)
      .to(main, { alpha: 1, duration: 0.20, ease: 'power2.out' }, 0.04)
      .to(main.scale, { x: 1, y: 1, duration: 0.42, ease: 'back.out(1.8)' }, 0.04);

    this.overlayText = group;
  }

  /** Tear down any dramatic / countdown overlay and any pending accolade
   *  texts. Called when the engine is detached so the next run starts
   *  with a clean accolades layer. */
  clearOverlays(): void {
    if (this.overlayText) { this.overlayText.destroy({ children: true }); this.overlayText = null; }
    // Kill any still-animating accolade popups too.
    while (this.accoladesLayer.children.length > 0) {
      const c = this.accoladesLayer.children[0]!;
      try { c.destroy({ children: true }); } catch { /* */ }
    }
    this.accoladeStaggerCounter = 0;
  }

  /** Spawn an accolade text (combo, t-spin, etc.) that floats up + fades. */
  /** Pop an accolade text near a board position (col, row in board cells).
   *  If `cells` is omitted, falls back to the board centre. The text floats
   *  up + fades quickly in Hack font. Multiple accolades stagger vertically. */
  private accoladeStaggerCounter = 0;
  accolade(text: string, color: string, size: number, lifeMs: number, ascend: number, cells?: { col: number; row: number }): void {
    const cellPx = this.options.cellPx;
    // Bugvirus glyphs are wider than Hack — scale ~30% down so the
    // on-board accolade pop doesn't dwarf the playfield.
    const pixelSize = Math.round(size * 0.72);
    const t = new Text({
      text,
      style: {
        fontFamily: '"Bugvirus", "CS Modley Pixel", Hack, monospace',
        fontSize: pixelSize,
        fontWeight: '400',
        fill: color,
        align: 'center',
        letterSpacing: 0,
        stroke: { color: 0x000000, width: 3, alpha: 0.85 },
        dropShadow: { color, alpha: 0.7, blur: 10, distance: 0 },
      },
    });
    t.anchor.set(0.5);
    if (cells) {
      t.x = (cells.col + 0.5) * cellPx;
      t.y = Math.max(size * 0.6, cells.row * cellPx - size * 0.3);
    } else {
      t.x = (BOARD_WIDTH * cellPx) / 2;
      t.y = (BOARD_VISIBLE_HEIGHT * cellPx) / 2;
    }
    // Stagger sequential accolades upward so they don't overlap.
    const stagger = (this.accoladeStaggerCounter % 4) * size * 0.95;
    this.accoladeStaggerCounter++;
    t.y -= stagger;
    // Constrain to the visible board area so it never spawns above the top edge.
    t.x = Math.max(size * 0.5, Math.min(BOARD_WIDTH * cellPx - size * 0.5, t.x));
    const startY = Math.max(size * 0.4, t.y);
    t.y = startY;
    t.alpha = 0;
    this.accoladesLayer.addChild(t);
    gsap.timeline()
      .to(t, { alpha: 1, y: t.y - ascend * 0.20, duration: 0.10, ease: 'power3.out' })
      .to(t, { y: t.y - ascend, duration: (lifeMs - 400) / 1000, ease: 'power2.out' }, '<')
      .to(t.scale, { x: 1.06, y: 1.06, duration: 0.16, ease: 'back.out(2)' }, '<')
      .to(t, { alpha: 0, duration: 0.3, ease: 'power2.in' }, '>-0.20')
      .call(() => {
        t.destroy();
        if (this.accoladesLayer.children.length === 0) this.accoladeStaggerCounter = 0;
      });
  }

  // -------- Layout --------

  /** Cached layout data so updateHold / updateNext don't drift from layout(). */
  private layoutState!: {
    boardX: number; boardY: number; boardW: number; boardH: number;
    sideW: number; gap: number;
    holdX: number; holdY: number; holdW: number; holdH: number;
    nextX: number; nextY: number; nextW: number;
    firstSlotH: number; restSlotH: number; slotGap: number;
    sideCellMax: number;
  };
  /** Read-only view onto layoutState for siblings (versus-mode line-send
   *  needs the board rect in stage coords to draw projectiles between fields). */
  get layoutStateView(): { boardX: number; boardY: number; boardW: number; boardH: number; nextX: number; nextY: number; nextW: number; queueBottom: number; holdX: number; metricsBottom: number } {
    const ls = this.layoutState;
    // Bottom edge of the last preview slot — DOM overlays anchor below
    // this so they never overlap the queue.
    const n = Math.max(1, this.options.previewCount);
    const queueBottom = ls.nextY + ls.firstSlotH + (n - 1) * (ls.slotGap + ls.restSlotH);
    return {
      boardX: ls.boardX, boardY: ls.boardY, boardW: ls.boardW, boardH: ls.boardH,
      nextX: ls.nextX, nextY: ls.nextY, nextW: ls.nextW, queueBottom, holdX: ls.holdX,
      metricsBottom: this.metricsBottomY,
    };
  }

  /** Recompute pixel layout based on cellPx + sidebar widths. Returns a content size. */
  layout(): { width: number; height: number; boardX: number; boardY: number; boardW: number; boardH: number } {
    const cellPx = this.options.cellPx;
    const boardW = cellPx * BOARD_WIDTH;
    const boardH = cellPx * BOARD_VISIBLE_HEIGHT;
    // Side panels are sized in CELL units so they scale nicely with cellPx.
    // 4 cells wide fits the I-tetromino preview at ~70% of game cell.
    const sideW = (this.options.showHold || this.options.showNext) ? Math.round(cellPx * 4) : 0;
    const gap = Math.round(cellPx * 0.6);
    const leftPad = this.options.showHold ? sideW + gap : 0;
    const rightPad = this.options.showNext ? sideW + gap : 0;
    const totalW = leftPad + boardW + rightPad;
    const boardX = leftPad;
    const boardY = 0;
    // Layer positions
    this.boardBg.x = boardX; this.boardBg.y = boardY;
    this.boardFrame.x = boardX; this.boardFrame.y = boardY;
    this.gridLines.x = boardX; this.gridLines.y = boardY;
    this.cellsLayer.x = boardX; this.cellsLayer.y = boardY;
    this.ghostLayer.x = boardX; this.ghostLayer.y = boardY;
    this.pieceLayer.x = boardX; this.pieceLayer.y = boardY;
    this.flashLayer.x = boardX; this.flashLayer.y = boardY;
    this.effectsLayer.x = boardX; this.effectsLayer.y = boardY;
    this.accoladesLayer.x = boardX; this.accoladesLayer.y = boardY;
    // Redraw frame + grid
    this.redrawBoardChrome(boardW, boardH, cellPx);
    // Board mask — same rect as the board interior; clips piece/ghost/cells.
    this.boardMask.clear();
    this.boardMask.rect(boardX, boardY, boardW, boardH).fill(0xffffff);

    // HOLD geometry — 3 cells tall, aligned with the very top of the
    // board. The mode badge now lives inside the feed-box header below
    // (see layoutHud), so we don't need to reserve space above the hold.
    const holdH = Math.round(cellPx * 3);
    const holdW = sideW;
    const holdX = boardX - holdW - gap;
    const holdY = 0;
    if (this.options.showHold) {
      this.holdBg.clear();
      this.holdBg.x = holdX; this.holdBg.y = holdY;
      // Fully opaque fill so the filtered scene behind hudRoot can't
      // bleed through and visually re-introduce grain / bloom on the
      // sidebar boxes. The text on top renders crisp either way; this
      // is purely about killing the backdrop bleed-through.
      this.holdBg
        .roundRect(0, 0, holdW, holdH, 6)
        .fill({ color: parseInt(this.palette.bg2.slice(1), 16), alpha: 1.0 })
        .roundRect(0.5, 0.5, holdW - 1, holdH - 1, 6)
        .stroke({ width: 1, color: parseInt(this.palette.accent2.slice(1), 16), alpha: 0.55 });
      // Header sits INSIDE the hold box at the top — matches the feed +
      // stats box header layout for a uniform sidebar rhythm.
      this.holdLabel.x = holdX + 8;
      this.holdLabel.y = holdY + 6;
    }

    // NEXT geometry — narrow stack on the right side of the board.
    const nextX = boardX + boardW + gap;
    const nextY = 0;
    const nextW = sideW;
    // Slot heights — first slightly taller for visual hierarchy. Total must
    // fit within the board height; we leave some spare so the panel reads as
    // anchored to the top rather than spanning the full board.
    const slotGap = Math.round(cellPx * 0.15);
    const firstSlotH = Math.round(cellPx * 2.8);
    const remainingH = Math.round(cellPx * BOARD_VISIBLE_HEIGHT * 0.62) - firstSlotH;
    const restCount = Math.max(1, this.options.previewCount - 1);
    const restSlotH = Math.max(Math.round(cellPx * 1.6),
      Math.floor((remainingH - slotGap * restCount) / restCount));

    // Cap preview cell sizes so they never look bigger than game cells.
    const sideCellMax = Math.floor(cellPx * 0.7);

    this.layoutState = {
      boardX, boardY, boardW, boardH, sideW, gap,
      holdX, holdY, holdW, holdH,
      nextX, nextY, nextW,
      firstSlotH, restSlotH, slotGap, sideCellMax,
    };

    if (this.options.showHud) this.layoutHud(boardX, boardY, boardW, boardH, cellPx, sideW, gap);
    return { width: totalW, height: boardH, boardX, boardY, boardW, boardH };
  }

  private redrawBoardChrome(boardW: number, boardH: number, _cellPx: number): void {
    this.boardBg.clear();
    this.boardBg.roundRect(0, 0, boardW, boardH, 4).fill({ color: parseInt(this.palette.bg2.slice(1), 16), alpha: 0.55 });
    this.boardFrame.clear();
    this.boardFrame.roundRect(0.5, 0.5, boardW - 1, boardH - 1, 3).stroke({ width: 1, color: parseInt(this.palette.accent.slice(1), 16), alpha: 0.45 });
    // Subtle outer halo via a softer second stroke
    this.boardFrame.roundRect(-2, -2, boardW + 4, boardH + 4, 5).stroke({ width: 1, color: parseInt(this.palette.accent.slice(1), 16), alpha: 0.10 });
    // Grid
    this.gridLines.clear();
    if (this.renderOpts.showGrid) {
      const cellPx = this.options.cellPx;
      const color = parseInt(this.palette.grid.slice(1), 16);
      // Inset endpoints by 1px so the stroke's half-width doesn't bleed
      // past the board frame — was extending ~0.5px on every edge.
      const boardW = BOARD_WIDTH * cellPx;
      const boardH = BOARD_VISIBLE_HEIGHT * cellPx;
      for (let x = 1; x < BOARD_WIDTH; x++) {
        const px = x * cellPx + 0.5;
        this.gridLines.moveTo(px, 1).lineTo(px, boardH - 1);
      }
      for (let y = 1; y < BOARD_VISIBLE_HEIGHT; y++) {
        const py = y * cellPx + 0.5;
        this.gridLines.moveTo(1, py).lineTo(boardW - 1, py);
      }
      this.gridLines.stroke({ width: 1, color, alpha: 0.55 });
    }
  }

  private layoutNext(x: number, w: number, cellPx: number, boardH: number): void {
    this.nextBg.x = x;
    this.nextBg.y = 0;
    // We draw individual slot graphics below.
  }

  private layoutHud(
    boardX: number, boardY: number, _boardW: number, _boardH: number,
    cellPx: number, sideW: number, gap: number,
  ): void {
    const x = boardX - sideW - gap;
    const lh = cellPx * 0.5;
    const headerH = 18;       // px reserved at the top of each box for its label + divider
    const boxGap = Math.round(cellPx * 0.5);  // equal spacing between hold / feed / metrics
    this.modeText.visible = false;
    this.modeValueText.visible = false;
    this.comboChip.visible = false;
    this.b2bChip.visible = false;

    const holdH = Math.round(cellPx * 3);  // matches the hold-box geometry in layout()
    const accentInt = parseInt(this.palette.accent2.slice(1), 16);
    const bg2Int = parseInt(this.palette.bg2.slice(1), 16);

    // Compact HUD (AI playfields in versus modes). Hide the feed +
    // stats boxes entirely; the AI sidebar only carries the hold box
    // + a single 'incoming' chip showing pressure the AI has queued.
    // Keeps the multi-AI layouts from bleeding text over the boards.
    if (this.options.compact) {
      this.feedBg.visible = false;
      this.feedHeaderText.visible = false;
      this.metricsBg.visible = false;
      this.statsHeaderText.visible = false;
      // Only the incoming counter is interesting — hide the rest.
      this.metricValueText.visible = true;
      this.metricLabelText.visible = true;
      for (const d of this.detailLines) { d.label.visible = false; d.value.visible = false; }
      // Stack: hold (already positioned by layout()), then a compact
      // 'incoming +N' line right below.
      const tx = x + 4;
      const cy = holdH + boxGap + 2;
      this.metricLabelText.x = tx;
      this.metricLabelText.y = cy;
      this.metricValueText.x = tx;
      this.metricValueText.y = cy + lh * 0.9;
      return;
    }

    // Player sidebar — three boxes stacked top-down with uniform spacing:
    //   1. Hold box     y = 0 .. holdH                (header: 'held' / 'locked')
    //   2. Feed box     starts at holdH + boxGap       (header: 'mode: <name>')
    //   3. Metrics box  starts at feed bottom + boxGap (header: 'stats')
    this.feedBg.visible = true;
    this.feedHeaderText.visible = true;
    this.metricsBg.visible = true;
    this.statsHeaderText.visible = true;
    this.metricValueText.visible = true;
    this.metricLabelText.visible = true;
    for (const d of this.detailLines) { d.label.visible = true; d.value.visible = true; }

    // ── Feed box ──────────────────────────────────────────────────────
    const feedX = x;
    const feedY = holdH + boxGap;
    const feedW = sideW;
    const feedH = Math.round(cellPx * 4.0);
    this.feedBg.clear();
    this.feedBg.x = feedX; this.feedBg.y = feedY;
    this.feedBg
      .roundRect(0, 0, feedW, feedH, 6)
      .fill({ color: bg2Int, alpha: 1.0 })
      .roundRect(0.5, 0.5, feedW - 1, feedH - 1, 6)
      .stroke({ width: 1, color: accentInt, alpha: 0.55 });
    // Header divider — thin accent line under the 'mode: <name>' label
    // separating the header from the feed entries below.
    this.feedBg
      .moveTo(8, headerH + 2)
      .lineTo(feedW - 8, headerH + 2)
      .stroke({ width: 1, color: accentInt, alpha: 0.35 });
    this.feedHeaderText.x = feedX + 8;
    this.feedHeaderText.y = feedY + 6;

    // ── Metrics box (with 'stats' header) ─────────────────────────────
    const metricsX = x;
    const metricsY = feedY + feedH + boxGap;
    const metricsW = sideW;
    // Header + padding + 4 detail rows + metric label/value
    const metricsContentH = lh * 1.0 + lh * 1.9 + 4 * (lh * 1.0 + lh * 1.6);
    const metricsH = Math.round(headerH + 8 + metricsContentH + 10);
    this.metricsBottomY = metricsY + metricsH;
    this.metricsBg.clear();
    this.metricsBg.x = metricsX; this.metricsBg.y = metricsY;
    this.metricsBg
      .roundRect(0, 0, metricsW, metricsH, 6)
      .fill({ color: bg2Int, alpha: 1.0 })
      .roundRect(0.5, 0.5, metricsW - 1, metricsH - 1, 6)
      .stroke({ width: 1, color: accentInt, alpha: 0.55 });
    this.metricsBg
      .moveTo(8, headerH + 2)
      .lineTo(metricsW - 8, headerH + 2)
      .stroke({ width: 1, color: accentInt, alpha: 0.35 });
    this.statsHeaderText.x = metricsX + 8;
    this.statsHeaderText.y = metricsY + 6;

    let cy = metricsY + headerH + 8;
    const tx = metricsX + 8;
    this.metricLabelText.x = tx; this.metricLabelText.y = cy; cy += lh * 1.0;
    this.metricValueText.x = tx; this.metricValueText.y = cy; cy += lh * 1.9;
    for (const d of this.detailLines) {
      d.label.x = tx; d.label.y = cy; cy += lh * 1.0;
      d.value.x = tx; d.value.y = cy; cy += lh * 1.6;
    }
  }

  // -------- Per-frame update --------

  update(dtMs: number): void {
    if (!this.engine) return;
    this.updateShake(dtMs);
    this.updateStamped();
    this.updateGhost();
    this.updateActivePiece(dtMs);
    this.updateHold();
    this.updateNext();
    // HUD text is owned by the App via setHudData() — don't touch it here.
    if (this.flashTtl > 0) this.flashTtl = Math.max(0, this.flashTtl - dtMs);
  }

  private updateShake(dtMs: number): void {
    if (this.shakeTtl > 0) {
      this.shakeTtl = Math.max(0, this.shakeTtl - dtMs);
      const t = this.shakeTtl / 220;
      const dx = (Math.random() - 0.5) * this.shakeAmount * t;
      const dy = (Math.random() - 0.5) * this.shakeAmount * t;
      this.root.x = this.shakeBaseX + dx;
      this.root.y = this.shakeBaseY + dy;
      if (this.shakeTtl === 0) {
        this.shakeAmount = 0;
        this.root.x = this.shakeBaseX;
        this.root.y = this.shakeBaseY;
      }
    }
  }

  setBasePosition(x: number, y: number): void {
    this.shakeBaseX = x; this.shakeBaseY = y;
    this.root.x = x; this.root.y = y;
  }

  private updateStamped(): void {
    if (!this.engine) return;
    const cellPx = this.options.cellPx;
    for (let y = 0; y < BOARD_VISIBLE_HEIGHT; y++) {
      const row = this.stampedSprites[y]!;
      for (let x = 0; x < BOARD_WIDTH; x++) {
        const c = this.engine.board.at(x, y + BOARD_BUFFER_HEIGHT);
        const existing = row[x];
        if (c === null) {
          if (existing) { existing.destroy(); row[x] = null; }
          continue;
        }
        if (!existing) {
          const tex = this.textures.pieces[c];
          const sp = new Sprite(tex);
          sp.width = cellPx; sp.height = cellPx;
          sp.x = x * cellPx; sp.y = y * cellPx;
          this.cellsLayer.addChild(sp);
          row[x] = sp;
        } else {
          // Update texture if cell changed color (rare but possible with garbage upgrades).
          const tex = this.textures.pieces[c];
          if (existing.texture !== tex) existing.texture = tex;
          existing.width = cellPx; existing.height = cellPx;
          existing.x = x * cellPx; existing.y = y * cellPx;
        }
      }
    }
  }

  private clearStamped(): void {
    for (let y = 0; y < BOARD_VISIBLE_HEIGHT; y++) {
      const row = this.stampedSprites[y]!;
      for (let x = 0; x < BOARD_WIDTH; x++) {
        if (row[x]) { row[x]!.destroy(); row[x] = null; }
      }
    }
  }

  private updateActivePiece(_dtMs: number): void {
    if (!this.engine || !this.engine.piece) {
      if (this.activeSprites.length) {
        for (const s of this.activeSprites) s.destroy();
        this.activeSprites = [];
      }
      return;
    }
    const piece = this.engine.piece;
    const cellPx = this.options.cellPx;

    // New piece spawn: snap visual to logical
    if (this.engine.pieceSeq !== this.lastSeq) {
      this.lastSeq = this.engine.pieceSeq;
      this.moveTween?.kill(); this.moveTween = null;
      this.rotateTween?.kill(); this.rotateTween = null;
      this.visual.x = piece.x;
      this.visual.y = piece.y - BOARD_BUFFER_HEIGHT;
      this.rotationVisual = 0;
      this.rebuildActiveSprites(piece.type);
      // Quick pulse on spawn for visibility
      this.pieceLayer.alpha = 0.3;
      gsap.to(this.pieceLayer, { alpha: 1, duration: 0.22, ease: 'power2.out' });
    } else {
      // Detect rotation change — snap sprites to new rotation, no scale pulse.
      // Rotations should feel instant so the player perceives them as a decisive
      // input, not an animation cycle.
      const tgtRotKey = piece.rotation;
      if ((this as unknown as { lastRot?: Rotation }).lastRot !== tgtRotKey) {
        this.rebuildActiveSprites(piece.type);
        (this as unknown as { lastRot?: Rotation }).lastRot = tgtRotKey;
      }
    }

    // Glide visual toward logical. Lateral moves (shift) and gravity (soft drop /
    // natural fall) both go through this lerp so movement reads as smooth.
    // Vertical lerp is faster than horizontal so soft-drop / gravity feel
    // responsive rather than draggy.
    const targetX = piece.x;
    const targetY = piece.y - BOARD_BUFFER_HEIGHT;
    if (this.visual.x !== targetX || this.visual.y !== targetY) {
      this.moveTween?.kill();
      // Different durations for the two axes — x glides, y falls.
      const distX = Math.abs(targetX - this.visual.x);
      const distY = Math.abs(targetY - this.visual.y);
      // Big y deltas (soft-drop bursts) snap to avoid laggy "follow" feel.
      if (distY > 3) {
        this.visual.y = targetY;
      }
      this.moveTween = gsap.to(this.visual, {
        x: targetX, y: targetY,
        duration: distX > 0 ? 0.05 : 0.035,
        ease: 'power3.out', overwrite: true,
      });
    }

    // Lock-delay pulse: grounded piece gets a subtle brightness oscillation.
    const grounded = this.engine.board.collides({ ...piece, y: piece.y + 1 });
    if (grounded) {
      const t = (this.engine.touchedMs ?? 0) / LOCK_DELAY_MS;
      const pulse = 0.85 + 0.15 * (0.5 + 0.5 * Math.sin(t * Math.PI * 3));
      this.pieceLayer.alpha = pulse;
    } else {
      this.pieceLayer.alpha = 1;
    }

    // pieceLayer is already positioned at (boardX, boardY) via layout().
    // Child sprites are placed at cell positions inside the board's local space.
    const shape = shapeOf(piece.type, piece.rotation);
    for (let i = 0; i < this.activeSprites.length; i++) {
      const sp = this.activeSprites[i]!;
      const [dx, dy] = shape[i]!;
      sp.x = (this.visual.x + dx) * cellPx;
      sp.y = (this.visual.y + dy) * cellPx;
      sp.width = cellPx; sp.height = cellPx;
    }
  }

  private rebuildActiveSprites(type: PieceType): void {
    for (const s of this.activeSprites) s.destroy();
    this.activeSprites = [];
    const tex = this.textures.pieces[type];
    for (let i = 0; i < 4; i++) {
      const sp = new Sprite(tex);
      this.pieceLayer.addChild(sp);
      this.activeSprites.push(sp);
    }
  }

  private updateGhost(): void {
    if (!this.engine || !this.engine.piece || !this.renderOpts.showGhost) {
      for (const s of this.ghostSprites) s.destroy();
      this.ghostSprites = [];
      return;
    }
    const piece = this.engine.piece;
    const distance = this.engine.board.dropDistance(piece);
    if (distance <= 0) {
      for (const s of this.ghostSprites) s.destroy();
      this.ghostSprites = [];
      return;
    }
    const cellPx = this.options.cellPx;
    const tex = this.textures.ghosts[piece.type];
    const shape = shapeOf(piece.type, piece.rotation);
    while (this.ghostSprites.length < 4) {
      const sp = new Sprite(tex);
      this.ghostLayer.addChild(sp);
      this.ghostSprites.push(sp);
    }
    // Ghost uses the LOGICAL piece position (not visual) so it always shows
    // the true hard-drop landing spot. If we used the lerping visual.x with
    // a distance computed at logical.x, the ghost can overlap stacked cells
    // mid-glide — the user reported that as bricks being "underneath" the ghost.
    const lx = piece.x;
    const ly = piece.y - BOARD_BUFFER_HEIGHT;
    for (let i = 0; i < 4; i++) {
      const sp = this.ghostSprites[i]!;
      const [dx, dy] = shape[i]!;
      sp.texture = tex;
      sp.x = (lx + dx) * cellPx;
      sp.y = (ly + dy + distance) * cellPx;
      sp.width = cellPx; sp.height = cellPx;
      sp.alpha = this.renderOpts.ghostAlpha * 4;
    }
  }

  // ---- HUD ----

  private updateHold(): void {
    if (!this.engine || !this.options.showHold) return;
    // Clear existing piece sprites
    for (const s of this.holdSprites) s.destroy();
    this.holdSprites = [];
    if (!this.engine.holdPiece) {
      // Empty slot — hide the lock icon + reset the label.
      this.holdLockSprite.visible = false;
      this.holdLabel.text = 'held';
      this.holdLabel.style.fill = this.palette.textDim;
      return;
    }
    const ls = this.layoutState;
    const type = this.engine.holdPiece;
    const tex = this.textures.pieces[type];
    const shape = shapeOf(type, 0 as Rotation);
    let minX = 9, maxX = -1, minY = 9, maxY = -1;
    for (const [dx, dy] of shape) {
      if (dx < minX) minX = dx;
      if (dx > maxX) maxX = dx;
      if (dy < minY) minY = dy;
      if (dy > maxY) maxY = dy;
    }
    const wc = maxX - minX + 1;
    const hc = maxY - minY + 1;
    const padInner = 10;
    const cell = Math.min(
      ls.sideCellMax,
      Math.floor(Math.min((ls.holdW - padInner * 2) / wc, (ls.holdH - padInner * 2) / hc)),
    );
    const ox = ls.holdX + (ls.holdW - wc * cell) / 2 - minX * cell;
    const oy = ls.holdY + (ls.holdH - hc * cell) / 2 - minY * cell;
    for (const [dx, dy] of shape) {
      const sp = new Sprite(tex);
      sp.width = sp.height = cell;
      sp.x = ox + dx * cell;
      sp.y = oy + dy * cell;
      sp.alpha = this.engine.holdLocked ? 0.45 : 1;
      this.hudLayer.addChild(sp);
      this.holdSprites.push(sp);
    }
    // Lock-state icon — sits at the RIGHT of the hold-box header, paired
    // with the 'held' / 'locked' text label on the left. The label text
    // flips to 'locked' when the slot has been used this piece, the
    // icon swaps to the closed-lock texture and tints to palette.danger.
    const locked = this.engine.holdLocked;
    const lockTex = locked ? getHoldLockClosedTex() : getHoldLockOpenTex();
    this.holdLockSprite.texture = lockTex;
    this.holdLockSprite.visible = true;
    this.holdLockSprite.x = ls.holdX + ls.holdW - 18;
    this.holdLockSprite.y = ls.holdY + 4;
    this.holdLockSprite.tint = parseInt((locked ? this.palette.danger : this.palette.accent).slice(1), 16);
    this.holdLabel.text = locked ? 'locked' : 'held';
    this.holdLabel.style.fill = locked ? this.palette.danger : this.palette.textDim;
  }

  private updateNext(): void {
    if (!this.engine || !this.options.showNext) return;
    const ls = this.layoutState;
    const queue = this.engine.bag.peek(this.options.previewCount);
    // Clear sprite + slot background graphics
    for (const arr of this.nextSpritesPerSlot) for (const s of arr) s.destroy();
    this.nextSpritesPerSlot = [];
    for (const g of this.nextLabels) g.destroy();
    this.nextLabels = [];
    let cy = ls.nextY;
    for (let i = 0; i < this.options.previewCount; i++) {
      const type = queue[i];
      if (!type) continue;
      const isFirst = i === 0;
      const h = isFirst ? ls.firstSlotH : ls.restSlotH;
      const a = isFirst
        ? { piece: 1.0, fill: 0.85, border: 0.75 }
        : {
          piece: Math.max(0.18, Math.pow(0.70, i)),
          fill: Math.max(0.18, 0.70 * Math.pow(0.78, i - 1)),
          border: Math.max(0.06, 0.40 * Math.pow(0.65, i - 1)),
        };
      const slotG = new Graphics();
      slotG.x = ls.nextX;
      slotG.y = cy;
      slotG
        .roundRect(0, 0, ls.nextW, h, 4)
        .fill({ color: parseInt(this.palette.bg2.slice(1), 16), alpha: a.fill })
        .roundRect(0.5, 0.5, ls.nextW - 1, h - 1, 4)
        .stroke({ width: 1, color: parseInt(this.palette.accent.slice(1), 16), alpha: a.border });
      this.hudLayer.addChild(slotG);
      this.nextLabels.push(slotG);
      // Piece sprites — cell size capped so preview cells never look bigger than game cells.
      const tex = this.textures.pieces[type];
      const shape = shapeOf(type, 0 as Rotation);
      let minX = 9, maxX = -1, minY = 9, maxY = -1;
      for (const [dx, dy] of shape) {
        if (dx < minX) minX = dx;
        if (dx > maxX) maxX = dx;
        if (dy < minY) minY = dy;
        if (dy > maxY) maxY = dy;
      }
      const wc = maxX - minX + 1;
      const hc = maxY - minY + 1;
      const padInner = isFirst ? 12 : 10;
      const cap = isFirst ? ls.sideCellMax : Math.floor(ls.sideCellMax * 0.85);
      const cell = Math.min(
        cap,
        Math.floor(Math.min((ls.nextW - padInner * 2) / wc, (h - padInner * 2) / hc)),
      );
      const ox = ls.nextX + (ls.nextW - wc * cell) / 2 - minX * cell;
      const oy = cy + (h - hc * cell) / 2 - minY * cell;
      const sprs: Sprite[] = [];
      for (const [dx, dy] of shape) {
        const sp = new Sprite(tex);
        sp.width = sp.height = cell;
        sp.x = ox + dx * cell;
        sp.y = oy + dy * cell;
        sp.alpha = a.piece;
        this.hudLayer.addChild(sp);
        sprs.push(sp);
      }
      this.nextSpritesPerSlot.push(sprs);
      cy += h + ls.slotGap;
    }
  }

  /** Push HUD text from the App (mode name + metric) into this playfield.
   *  Mode label scale is cached so we don't thrash Pixi text measurement
   *  every frame — the modeName changes rarely, the metric value changes
   *  often. */
  private lastModeName = '';
  private lastModeSidebarW = -1;
  setHudData(d: {
    modeName: string;
    metricLabel: string;
    metricValue: string;
  }): void {
    const mode = d.modeName.toLowerCase();
    const sideW = this.options.cellPx * 4 - 8;
    if (mode !== this.lastModeName || sideW !== this.lastModeSidebarW) {
      // Mode name renders inside the feed-box header now.
      this.feedHeaderText.text = `mode: ${mode}`;
      // Auto-fit width — long difficulty names ("nightmare") would
      // otherwise overflow the compact versus sidebar. Scale preserves
      // alignment without re-laying out the rest of the HUD.
      const naturalW = this.feedHeaderText.width;
      const s = naturalW > 0 ? Math.min(1, (sideW - 14) / naturalW) : 1;
      this.feedHeaderText.scale.set(Math.max(0.55, s));
      this.lastModeName = mode;
      this.lastModeSidebarW = sideW;
    }
    this.metricLabelText.text = d.metricLabel.toLowerCase();
    this.metricValueText.text = d.metricValue;
    if (this.engine) {
      this.detailLines[0]!.value.text = `${this.engine.stats.level}`;
      this.detailLines[1]!.value.text = `${this.engine.stats.lines}`;
      this.detailLines[2]!.value.text = `${this.engine.stats.pieces}`;
      const pps = this.engine.stats.pieces / Math.max(1, this.engine.stats.elapsedMs / 1000);
      this.detailLines[3]!.value.text = pps.toFixed(2);
      this.comboChip.text = this.engine.stats.combo >= 1 ? `combo x${this.engine.stats.combo + 1}` : '';
      this.b2bChip.text = this.engine.stats.backToBack >= 1 ? `b2b x${this.engine.stats.backToBack}` : '';
    }
  }

  private makeText(text: string, size: number, fill: string, letterSpacing: number, weight: '400' | '500' | '600' | '700' | '800' = '600'): Text {
    return new Text({
      text,
      style: {
        fontFamily: 'Hack, ui-monospace, monospace',
        fontSize: size,
        fontWeight: weight,
        fill,
        letterSpacing: letterSpacing * size,
      },
    });
  }
}

/** Perceptual luminance (0..1) for a "#rrggbb" string. Used to choose a
 *  contrasting stroke/shadow color for overlay text so it stays readable
 *  on either light or dark fills. */
function textLuminance(hex: string): number {
  const v = parseInt(hex.replace('#', '').slice(0, 6), 16);
  const r = ((v >> 16) & 0xff) / 255;
  const g = ((v >> 8) & 0xff) / 255;
  const b = (v & 0xff) / 255;
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}
